// Input of variable_mapping is array of object. each object looks like: {
// Theme: "Demography",
// table_name: "Demographics",
// var_id: "dem_01",
// description:"Age",
// var_values: "Age - 18 to 19 years",
// national_count:1062391,
// value_id: 10011001,
// table_id:"dem" }
// access: 1 if access allowed or 0 otherwise

/**
 * Gets a list of themes with respective tables (aka "Categories")
 * @param   {Array}  variable_mapping - reference to variable_mapping table
 * @returns {Array} array of objects like: {'theme': 'Demography', 'tables': ['dem', 'psy']}
 */
function lookForThemes(variable_mapping) {
  // object of objects which will be returned as array.
  const result = {};
  // array to check for duplicates. include tables from the result
  const duplicates = [];
  for (let i = 0, n = variable_mapping.length; i < n; i++) {
    const row = variable_mapping[i];
    // Check if table_id have been already added, and if not, add it to result
    if (!duplicates.includes(row.table_id) && row.table_id !== 'All') {
      if (result[row['theme']] === undefined) {
        result[row['theme']] = {
          theme: row['theme'],
          tables: [row.table_id],
          access: row['access'],
        };
      } else {
        // if a client have access to at list one category under the theme, the theme should not be locked
        if (result[row['theme']].access === 0 && row['access'] === 1) {
          result[row['theme']].access = 1;
        }
        result[row['theme']].tables.push(row.table_id);
      }
      duplicates.push(row.table_id);
    }
  }
  return Object.values(result);
}

/**
 * Retrieves list of columns(aka "Attributes") based on passed table (aka "Category")
 *
 * @param   {Array}  variable_mapping - reference to variable_mapping table
 * @param   {String} table - name of table/Theme, i.e: bee, foo, dem.
 * @returns {Array} array of objects like: {code: 'dem_01', name: 'Age'}
 */
function lookForColumns(variable_mapping, table) {
  // array of objects that will be returned
  const result = [];
  // array to check for duplicates. include 'codes' from the result
  const duplicates = [];
  for (let i = 0, n = variable_mapping.length; i < n; i++) {
    const row = variable_mapping[i];
    if (row.table_id === table && !duplicates.includes(row['var_id'])) {
      result.push({
        code: row['var_id'],
        name: row['description'],
        access: row['access'],
      });
      duplicates.push(row['var_id']);
    }
  }
  return result;
}

/**
 * Function to retrieve list of values (aka "Variables") for a respective code (aka "Attribute")
 * @param {Array}  variable_mapping - reference to variable_mapping table
 * @param {String} varCode - variable ("Attribute") code like: 'dem_01'
 * @param {Boolean} isSegment - if varCode is segment -> True, else False.
 * @returns {Array} array of objects like
 * {'code': 10011114, 'name': 'Family Income - Under $5,000', 'percentage': 0.0139944904789361}
 *
 */
function lookForValues(variable_mapping, { tabColumn: varCode, isSegment }) {
  // array of objects that will be returned
  const result = [];
  // const total population - temporary here.
  let totalPopulation = 0;
  for (let i = 0, n = variable_mapping.length; i < n; i++) {
    const row = variable_mapping[i];
    const rowCode = row['var_id'];
    if (rowCode === null || rowCode === 'None') {
      totalPopulation = row['national_count'];
    } else if (rowCode === varCode) {
      const code = row['value_id'];
      const attribute = row['description'];
      const name = row['var_values'];
      const population = row['national_count'];
      const access = row['access'];
      // Temporary work-around. Need to replace % with absolute values
      const percentage = row['national_count'] / totalPopulation;
      result.push({ code, attribute, name, percentage, population, access });
    }
  }
  return result;
}

/**
 * Retrieves a list of tables (aka "Categories") with codes.
 *
 * @param {Array} variable_mapping - a reference to variableMapping
 * @returns array of objects like: {'code': 'ari', 'name': 'Arima'}
 */
function searchTables(variable_mapping) {
  const seenTableIds = new Set();
  return variable_mapping.reduce((acc, row) => {
    if (!seenTableIds.has(row.table_id) && row.table_id !== 'All') {
      seenTableIds.add(row.table_id);
      acc.push({
        code: row.table_id,
        name: row.table_name,
        access: row.access,
      });
    }
    return acc;
  }, []);
}

// This function is used in sort, to sort search results by search weight

function compareSearchResults(a, b) {
  if (a.weight < b.weight) {
    return 1;
  }
  if (a.weight > b.weight) {
    return -1;
  }
  return 0;
}

// This function is used to calculate percentage of substring in a search string.

const countPercentageOfString = (searchString, searchLine) => {
  return Math.round((searchString.length / searchLine.length) * 100) / 100;
};

/**
 * Search variable_mapping by specified searchString. Used in Search Bars.
 *
 * @param {Array} variable_mapping - a reference to variableMapping
 * @param {String} searchString - search text from user input
 * @param {Boolean} isAdvanced  - if True searches Attributes, if False searches Variables
 * @returns {Array} - array of objects which includes non-duplicate values that include SearchString
 */
function searchVarsByText(variable_mapping, searchString, isAdvanced) {
  // array of objects that will be returned
  const result = [];
  // array to check for duplicates. include 'codes' from the result
  const duplicates = [];
  // A field in variable_mapping which is used to search for searchString.
  let searchField = '';
  // An array which determing fields to be retrived from each row of variabe_mapping with matching searchString
  let fieldsToInclude = [
    { tcode: 'table_id' },
    { tname: 'table_name' },
    { ccode: 'var_id' },
    { cname: 'description' },
    { access: 'access' },
    { vcode: 'value_id' },
    { vname: 'var_values' },
  ];
  // A value which is used to keep track of duplicates
  let duplicateValue = '';
  if (isAdvanced) {
    searchField = ['description', 'table_name', 'theme', 'var_values'];
    duplicateValue = 'description';
  } else {
    searchField = ['description', 'table_name', 'theme', 'var_values'];
    duplicateValue = 'value_id';
    fieldsToInclude = fieldsToInclude.concat([
      { population: 'national_count' },
      { audienceSize: 'national_count' },
    ]);
  }
  let totalPopulation = 0;
  // Go through each record in variable_mapping and check if specified fields matches searchString
  for (let i = 0, n = variable_mapping.length; i < n; i++) {
    const row = variable_mapping[i];
    let searchLine = '';
    for (let count = 0, n = searchField.length; count < n; count++) {
      searchLine = searchLine + ' ' + row[searchField[count]];
    }
    if (row.table_id === 'All') {
      totalPopulation = row['national_count'];
    } else if (searchLine.toLowerCase().includes(searchString.toLowerCase())) {
      if (!duplicates.includes(row[duplicateValue])) {
        duplicates.push(row[duplicateValue]);
        let newResult = {};
        let weight = 0;
        const searchStringLowerCase = searchString.toLowerCase();
        // This 'if' waterfall conditions look throught 4 Audience Builder steps
        // and depending on which category the search phrase has matches into, it assigns a weight
        // and then calculates percentage of length to better sort lately, so that
        // a result with var_value: 'Toronto' would be higher than 'Toronot Newspaper'
        // if a user tries to search for 'Toronto'
        if (row['description'].toLowerCase().includes(searchStringLowerCase)) {
          weight =
            0.5 + countPercentageOfString(searchString, row['description']);
        } else if (
          row['table_name'].toLowerCase().includes(searchStringLowerCase)
        ) {
          weight =
            0.6 + countPercentageOfString(searchString, row['table_name']);
        } else if (
          row['var_values'].toLowerCase().includes(searchStringLowerCase)
        ) {
          weight =
            0.8 + countPercentageOfString(searchString, row['var_values']);
        }
        // Go through fieldsToInclude and unwrap each individual object-key pair into one large object
        for (let j = 0, n = fieldsToInclude.length; j < n; j++) {
          const item = fieldsToInclude[j];
          const currentItemKey = Object.keys(item)[0];
          // This case is for backward compatability.
          if (currentItemKey === 'percentage') {
            newResult[currentItemKey] = row['national_count'] / totalPopulation;
          } else if (currentItemKey === 'audienceSize') {
            newResult[currentItemKey] = row['national_count'];
          } else {
            newResult[currentItemKey] = row[Object.values(item)[0]];
          }
        }
        newResult['weight'] = weight;
        result.push(newResult);
      }
    }
  }
  result.sort(compareSearchResults);
  return result.slice(0, 300);
}

export {
  lookForColumns,
  lookForThemes,
  lookForValues,
  searchTables,
  searchVarsByText,
};
